import React, { useEffect, useRef, useCallback, useState } from 'react';
import './heroSection.css';
import gsap from 'gsap';
// Tijdelijk hidden
// import Button from '../../Button/Button';

const HeroSection = ({ scrollToServices, titleTexts, paragraphTexts }) => {
    const gridRef = useRef(null);
    const contentSectionRef = useRef(null);
    let resizeTimeout = null;
    const animationStateRef = useRef({
        numRows: 10,
        numCols: 10,
        pixels: [],
        currentSlotIndex: 0,
        slotProgress: 0,
        isRunning: true,
        previousWidth: window.innerWidth
    });

    // const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const animationTimeoutRef = useRef(null);

    const animationSlots = [
        {
            direction: 'br-tl',
            numCurves: 2,
            burnDelay: 120,
            curveType: 'cos',
            frequency: 3,
            amplitude: 0.4,
            phaseShift: Math.PI,
            burningColors: [
                '#562D1F',
                '#863F28',
                '#AB4E2F',
                '#AB4E2F',
                '#863F28',
                '#562D1F'
            ],
            columnSpace: 0,
            rowSpace: 0,
            pixelRadius: [1, 2, 1],
            timeTillNext: 5,
        },
        {
            direction: 'bt',
            numCurves: 2,
            burnDelay: 120,
            curveType: 'cos',
            frequency: 4,
            amplitude: 0.7,
            phaseShift: Math.PI / 3,
            burningColors: [
                '#562D1F',
                '#863F28',
                '#AB4E2F',
                '#C25834',
                '#CF5D37',
                '#C25834',
                '#AB4E2F',
                '#863F28',
                '#562D1F'
            ],
            columnSpace: 1,
            rowSpace: 0,
            pixelRadius: 0,
            timeTillNext: 5,
        },
        {
            direction: 'tl-br',
            burnDelay: 120,
            numCurves: 2,
            curveType: 'sin',
            frequency: 2,
            amplitude: 0.2,
            phaseShift: -2.0,
            burningColors: [
                '#562D1F',
                '#863F28',
                '#AB4E2F',
                '#C25834',
                '#CF5D37',
                '#AB4E2F',
                '#863F28',
                '#562D1F'
            ],
            columnSpace: 1,
            rowSpace: 1,
            pixelRadius: [1, 2, 1],
            timeTillNext: 4.5,
        },
    ];

    const createGrid = useCallback(() => {
        const grid = gridRef.current;
        animationStateRef.current.pixels.forEach(pixel => pixel.remove());
        animationStateRef.current.pixels = [];

        for (let i = 0; i < animationStateRef.current.numRows * animationStateRef.current.numCols; i++) {
            const pixel = document.createElement('div');
            pixel.classList.add('pixel');
            grid.appendChild(pixel);
            animationStateRef.current.pixels.push(pixel);
        }
    }, []);

    const updatePixelSize = useCallback(() => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const sizeFactor = 0.029;
        const pixelSize = Math.floor(Math.sqrt(viewportWidth ** 2 + viewportHeight ** 2) * sizeFactor);

        animationStateRef.current.numCols = Math.floor(viewportWidth / pixelSize) + 1;
        const maxRows = Math.floor(viewportHeight / pixelSize);
        animationStateRef.current.numRows = maxRows + 4;

        const grid = gridRef.current;
        grid.style.gridTemplateColumns = `repeat(${animationStateRef.current.numCols}, ${pixelSize}px)`;
        grid.style.gridTemplateRows = `repeat(${animationStateRef.current.numRows}, ${pixelSize}px)`;

        createGrid();
    }, [createGrid]);

    const PixelMovement = useCallback((diagonalIndex, slot) => {
        const burningPixels = [];
        const { numCurves, curveType, frequency, amplitude, phaseShift, direction } = slot;
        const { numRows, numCols, pixels } = animationStateRef.current;

        for (let curve = 0; curve < numCurves; curve++) {
            for (let i = 0; i <= diagonalIndex; i++) {
                let row, col;

                switch (direction) {
                    case 'tl-br':
                        row = i;
                        col = diagonalIndex - i;
                        break;
                    case 'tr-bl':
                        row = i;
                        col = numCols - 1 - (diagonalIndex - i);
                        break;
                    case 'bl-tr':
                        row = numRows - 1 - i;
                        col = diagonalIndex - i;
                        break;
                    case 'br-tl':
                        row = numRows - 1 - i;
                        col = numCols - 1 - (diagonalIndex - i);
                        break;
                    case 'bt':
                        row = numRows - 1 - (diagonalIndex - i);
                        col = i;
                        break;
                    default:
                        row = i;
                        col = diagonalIndex - i;
                }

                let angle = (i / numRows) * frequency * Math.PI;
                angle += phaseShift;

                const trigValue = curveType === 'sin' ? Math.sin(angle) : Math.cos(angle);
                const curveEffect = Math.round(trigValue * amplitude * (numCols / 2));

                const adjustedCol = col + curveEffect + curve;

                if (row >= 0 && row < numRows && adjustedCol >= 0 && adjustedCol < numCols) {
                    burningPixels.push(pixels[row * numCols + adjustedCol]);
                }
            }
        }

        return burningPixels;
    }, []);

    function burnAnimation(slot, onComplete) {
        const { numRows, numCols } = animationStateRef.current;
        const totalDiagonals = numRows + numCols - 1;
        const { burnDelay, burningColors, columnSpace, rowSpace, pixelRadius } = slot;
        let diagonalIndex = 0;

        const interval = setInterval(() => {
            if (diagonalIndex < totalDiagonals) {
                const burningPixels = PixelMovement(diagonalIndex, slot);

                burningPixels.forEach((pixel) => {
                    const timeline = gsap.timeline();

                    burningColors.forEach((color, i) => {
                        timeline.to(pixel, {
                            backgroundColor: color,
                            marginRight: Array.isArray(columnSpace) ?
                                columnSpace[i % columnSpace.length] : columnSpace,
                            marginBottom: Array.isArray(rowSpace) ?
                                rowSpace[i % rowSpace.length] : rowSpace,
                            borderRadius: Array.isArray(pixelRadius) ?
                                pixelRadius[i % pixelRadius.length] : pixelRadius,
                            duration: 0.4,
                            delay: i * 0.05,
                            ease: 'power1.out',
                        }, i === 0 ? "<" : ">");
                    });

                    timeline.to(pixel, {
                        duration: 0.2,
                        ease: 'power1.in',
                        onComplete: () => {
                            pixel.style.backgroundColor = '';
                            pixel.style.marginRight = '';
                            pixel.style.marginBottom = '';
                            pixel.style.borderRadius = '';
                        }
                    });
                });

                diagonalIndex++;
            } else {
                clearInterval(interval);
                onComplete();
            }
        }, burnDelay);
    }

    // const animateTextOnLoad = useCallback(() => {
    //     gsap.to(contentSectionRef.current, {
    //         opacity: 1,
    //         duration: 2,
    //         ease: 'power2.inOut'
    //     });

    //     gsap.fromTo(
    //         '.content-section__inner > *',
    //         { opacity: 0 },
    //         {
    //             opacity: 1,
    //             stagger: 0.3,
    //             duration: 1.5,
    //             ease: 'power2.out'
    //         }
    //     );
    // }, []);

    const runAnimations = () => {
        const slotCount = animationSlots.length;
        let currentSlotIndex = 0;

        const runNextAnimation = () => {
            if (currentSlotIndex < slotCount) {
                const slot = animationSlots[currentSlotIndex];
                const { timeTillNext } = slot;

                burnAnimation(slot, () => {
                    // setCurrentTextIndex((prevIndex) => (prevIndex + 1) % titleTexts.length);

                    animationTimeoutRef.current = setTimeout(() => {
                        currentSlotIndex = (currentSlotIndex + 1) % slotCount;
                        animationTimeoutRef.current = setTimeout(runNextAnimation, timeTillNext * 1000);
                    }, 3000);
                });
            }
        };

        runNextAnimation();
    };

    function handleResize() {
        const currentWidth = window.innerWidth;

        if (currentWidth !== animationStateRef.current.previousWidth) {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const previousNumCols = animationStateRef.current.numCols;
                const previousNumRows = animationStateRef.current.numRows;

                updatePixelSize();

                if (
                    animationStateRef.current.numCols !== previousNumCols ||
                    animationStateRef.current.numRows !== previousNumRows
                ) {
                    window.location.reload();
                }
                animationStateRef.current.previousWidth = currentWidth;

            }, 200);
        }
    }

    useEffect(() => {
        createGrid();
        updatePixelSize();
        window.addEventListener('resize', handleResize);

        //animateTextOnLoad();
        runAnimations();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [createGrid, updatePixelSize]); // animeTextOnLoad verwijderd

    return (
        <div className="hero section--full-width">
            <div className="hero-grid-container">
                <div className="hero-grid" id="pixel-grid" ref={gridRef}></div>
            </div>
            {/* Tijdelijk uitgezet */}
            {/* <div className="content-section__inner">
                <h1 className="hero-title">{titleTexts[currentTextIndex]}</h1>
                <p className="hero-label">{paragraphTexts[currentTextIndex]}</p>
                <div className='button-container'>
                        <Button variant="link" onClick={() => scrollToServices()}>Bekijk onze expertises</Button>
                    </div>
            </div> */}
        </div>
    );
};

export default HeroSection;
